import { useMutation } from '@apollo/client';
import { ConsentManagerBuilder } from '@segment/consent-manager';
import { Destination, type CategoryPreferences } from '@segment/consent-manager/types/types';
import clsx from 'clsx';
import { DocumentNode } from 'graphql';
import { camelCase, isUndefined } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useState, type Dispatch, type KeyboardEvent, type SetStateAction } from 'react';

import { CookieStatistics } from '@pflegia/types/api/cookie-statistics/cookie-statistics';
import { useConsentBanner } from '@pflegia/ui/contexts/consent-banner.context';
import { useCookiesConsentManagerContext } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import useCookieStatistics, {
  DEFAULT_UPDATE_COOKIE_CONSENT_STATISTICS_MUTATION,
  shouldSendStatistics,
} from '@pflegia/ui/hooks/use-cookie-statistics';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import Button, { type ButtonProps } from '@pflegia/ui/shared/button';
import Modal from '@pflegia/ui/shared/modal/Modal';
import { SectionTitleBase } from '@pflegia/ui/shared/section/SectionTitleBase.component';
import Switch from '@pflegia/ui/shared/switch';

import { ADVERTISING_CATEGORIES, MARKETING_AND_ANALYTICS_CATEGORIES } from './categories';
import { handleMapCustomPreferences } from './map-custom-preferences';

const googlePrivacyPolicy = 'https://business.safety.google/privacy/';
const isModalBlocking = process.env.NEXT_PUBLIC_COOKIE_MODAL_BLOCKING === 'true';

type SetPreferences = (newPreferences: CategoryPreferences) => void;
type SaveConsent = (newPreferences?: boolean | CategoryPreferences | undefined, shouldReload?: boolean | undefined) => void;

interface SegmentConsentManagerProps {
  destinations: Destination[];
  preferences: CategoryPreferences;
  setPreferences: SetPreferences;
  saveConsent: SaveConsent;
  termsAndConditions: string;
  imprintAndPrivacy: string;
  cookieConsentStatisticsUpdateMutation?: DocumentNode;
  highlightColor?: string;
  hideConsentModal?: boolean;
}

interface AcceptButtonProps extends ButtonProps {
  saveConsent: SaveConsent;
  all?: boolean;
  close: VoidFunction;
  setPreferences?: SetPreferences;
}

interface CookieSettingsButtonProps
  extends ButtonProps,
    Pick<SegmentConsentManagerProps, 'cookieConsentStatisticsUpdateMutation' | 'highlightColor'> {
  setCookieMode: Dispatch<SetStateAction<boolean>>;
  setPreferences?: SetPreferences;
}

interface PreferenceSwitchProps {
  id: string;
  preferences: CategoryPreferences;
  setPreferences: SetPreferences;
}

interface SegmentConsentManagerBaseProps
  extends Pick<
    SegmentConsentManagerProps,
    'termsAndConditions' | 'imprintAndPrivacy' | 'cookieConsentStatisticsUpdateMutation' | 'highlightColor' | 'hideConsentModal'
  > {
  onAnalyticsReady?: (preferences: CategoryPreferences) => void;
}

const categoriesGroup = [
  { name: 'marketingAndAnalytics', elements: MARKETING_AND_ANALYTICS_CATEGORIES },
  { name: 'advertising', elements: ADVERTISING_CATEGORIES },
];

const acceptAll = (setPreferences: SetPreferences) => {
  const allPreferences = categoriesGroup.reduce((obj, item) => ({ ...obj, [item.name]: true }), {});
  setPreferences(allPreferences);
};

const AcceptButton: React.FC<AcceptButtonProps> = ({ close, saveConsent, all = false, setPreferences, ...rest }) => {
  const handleAcceptAll = useCallback(() => {
    if (all && setPreferences) acceptAll(setPreferences);
    saveConsent();
    close();
  }, [all, close, saveConsent, setPreferences]);

  return <Button onClick={handleAcceptAll} {...rest} />;
};

const CookieSettingsButton: React.FC<CookieSettingsButtonProps> = ({
  setCookieMode,
  label,
  cookieConsentStatisticsUpdateMutation,
  highlightColor,
  setPreferences,
  ...rest
}) => {
  const { showSettings } = useCookieStatistics(cookieConsentStatisticsUpdateMutation);

  const handleCookieSettings = useCallback(() => {
    setCookieMode(true);
    setPreferences?.({ advertising: true, marketingAndAnalytics: true });
    showSettings();
  }, [setCookieMode, setPreferences, showSettings]);

  return (
    <button onClick={handleCookieSettings} {...rest} className={clsx(highlightColor, 'text-sm underline sm:text-base')}>
      {label}
    </button>
  );
};

const BannerActions: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={clsx('mb4 mt-3 flex justify-between gap-6 sm:mt-8 sm:flex-wrap sm:justify-end', className)}>{children}</div>
);

const PreferenceSwitch: React.FC<PreferenceSwitchProps> = ({ id, preferences, setPreferences }) => {
  const handleOnChange = useCallback(() => {
    setPreferences({
      [id]: !preferences[id],
    });
  }, [id, preferences, setPreferences]);

  return <Switch id={id} checked={Boolean(preferences[id])} onChange={handleOnChange} />;
};

const SegmentConsentManager: React.FC<SegmentConsentManagerProps> = ({
  destinations,
  preferences,
  setPreferences,
  saveConsent,
  termsAndConditions,
  imprintAndPrivacy,
  cookieConsentStatisticsUpdateMutation,
  highlightColor = 'text-primary',
  hideConsentModal = false,
}) => {
  const context = useCookiesConsentManagerContext();
  const { setIsBannerOpen } = useConsentBanner();
  const { isPraxia } = useFlavorFromRoute();

  const { t } = useTranslation('common', { keyPrefix: 'segmentConsent' });
  const [cookieMode, setCookieMode] = useState(false);
  const hasSetPreferences = Object.keys(preferences).length > 0;
  const [showConsentModal, setShowConsentModal] = useState(!hasSetPreferences && !isUndefined(process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY));
  const isConsentModalVisible = showConsentModal && !hideConsentModal;

  const hideModal = useCallback(() => {
    setShowConsentModal(false);
  }, []);

  useEffect(() => {
    setIsBannerOpen(showConsentModal);
  }, [showConsentModal, setIsBannerOpen]);

  useEffect(() => () => setIsBannerOpen(false), [setIsBannerOpen]);

  const { setCookies, dismissCookies, showBanner } = useCookieStatistics(cookieConsentStatisticsUpdateMutation);

  const handleAccepted = useCallback(
    (all: boolean) => {
      saveConsent();
      setCookies(all || preferences);
    },
    [saveConsent, setCookies, preferences],
  );

  const handleDismiss = useCallback(() => {
    const newPreferences = { advertising: false, marketingAndAnalytics: false };
    saveConsent(newPreferences);
    setPreferences(newPreferences);
    setCookies(newPreferences);
    setShowConsentModal(false);
  }, [saveConsent, setCookies, setPreferences]);

  const handleModalClose = useCallback(() => {
    const grantedPreferences = { advertising: true, marketingAndAnalytics: true };
    saveConsent(grantedPreferences);
    setPreferences(grantedPreferences);
    dismissCookies();
    setShowConsentModal(false);
  }, [saveConsent, setPreferences, dismissCookies]);

  const handleDismissKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      handleDismiss();
    },
    [handleDismiss],
  );

  const dismissButtonA11yProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: handleDismiss,
    onKeyDown: handleDismissKeyDown,
  };

  useEffect(() => {
    if (isConsentModalVisible) {
      showBanner();
    } else if (context?.ready === false) {
      context?.updateCookiesSettings(preferences);
    }
  }, [context, preferences, showBanner, isConsentModalVisible]);

  const components = {
    TermsAndConditions: <Link className={highlightColor} href={termsAndConditions} target='_blank' />,
    Imprint: <Link className={highlightColor} href={imprintAndPrivacy} target='_blank' />,
    GooglePrivacy: <Link className={highlightColor} href={googlePrivacyPolicy} target='_blank' />,
    DismissButton: <span className={clsx(highlightColor, 'inline cursor-pointer hover:underline')} {...dismissButtonA11yProps} />,
  };

  const bannerContentKey = isPraxia ? 'praxiaBannerContent' : 'bannerContent';
  const preferencesDialogContentKey = isPraxia ? 'praxiaPreferencesDialogContent' : 'preferencesDialogContent';

  return (
    isConsentModalVisible && (
      <Modal
        onClose={isModalBlocking ? undefined : handleModalClose}
        containerClassName='z-cookie'
        contentWrapperClassName='h-full'
        className={
          cookieMode
            ? 'fixed top-0 left-0 h-full w-full overflow-y-auto px-0 pt-20 pb-0 sm:relative sm:top-auto sm:right-auto sm:left-auto sm:h-auto sm:pb-6'
            : 'pb-6'
        }>
        <div className='h-full lg:px-6' data-testid='cookie-modal'>
          {cookieMode ? (
            <div className='flex h-full flex-col justify-between'>
              <div className='px-8'>
                <SectionTitleBase t={t} titleKey='preferencesDialogTitle' />
                <p>
                  <Trans t={t} i18nKey={preferencesDialogContentKey} components={components} />
                </p>
                <div>
                  {categoriesGroup.map((category, categoryIndex) => (
                    <div key={`categories-group-${category.name}`}>
                      <hr className='mt-6 bg-gray-300' />

                      <div key={category.name} className='mt-6'>
                        <div className='flex justify-between'>
                          <span className='font-medium'>{t(`categories.${category.name}`)}</span>
                        </div>
                        <div className='flex justify-between'>
                          <div className='mt-2 w-9/12 text-sm'>{t(`descriptions.${camelCase(category.name)}`)}</div>
                          <PreferenceSwitch id={category.name} preferences={preferences} setPreferences={setPreferences} />
                        </div>
                        <div className='mt-2'>
                          {t('softwareUsed')}:{' '}
                          {destinations
                            .filter(destination =>
                              categoriesGroup[categoryIndex].elements
                                .map(categoryGroupItem => camelCase(categoryGroupItem))
                                .includes(camelCase(destination.category)),
                            )
                            .map((filteredDestination, destinationIndex, array) => (
                              <span key={filteredDestination.id}>
                                <Link className={clsx(highlightColor, 'underline')} href={filteredDestination.website}>
                                  {filteredDestination.name}
                                </Link>
                                {array.length !== destinationIndex + 1 && ', '}
                              </span>
                            ))}
                          {'. '}
                          <Trans t={t} i18nKey={`moreDetails.${camelCase(category.name)}`} components={components} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <BannerActions className='hidden sm:flex'>
                <AcceptButton saveConsent={() => handleAccepted(false)} close={hideModal} label={t('save')} />
              </BannerActions>

              <div className='sticky bottom-0 mt-2 flex flex-col sm:bottom-auto sm:mt-6 sm:hidden'>
                <AcceptButton
                  className='h-14 w-full rounded-none'
                  saveConsent={() => handleAccepted(false)}
                  close={hideModal}
                  label={t('save')}
                />
              </div>
            </div>
          ) : (
            <>
              <SectionTitleBase t={t} className='mb-3' titleKey='bannerTitle' />
              <div className='text-sm sm:text-lg'>
                <Trans t={t} i18nKey={bannerContentKey} components={components} />
              </div>
              <BannerActions className='xs:gap-6 gap-3'>
                <CookieSettingsButton
                  label={t('cookieSettings')}
                  setCookieMode={setCookieMode}
                  cookieConsentStatisticsUpdateMutation={cookieConsentStatisticsUpdateMutation}
                  highlightColor={highlightColor}
                  setPreferences={setPreferences}
                />
                <AcceptButton
                  data-testid='accept-cookies'
                  saveConsent={() => handleAccepted(true)}
                  close={hideModal}
                  label={t('acceptAll')}
                  setPreferences={setPreferences}
                  all
                />
              </BannerActions>
            </>
          )}
        </div>
      </Modal>
    )
  );
};

export const SegmentConsentManagerBase: React.FC<SegmentConsentManagerBaseProps> = ({
  onAnalyticsReady,
  termsAndConditions,
  imprintAndPrivacy,
  cookieConsentStatisticsUpdateMutation,
  highlightColor,
  hideConsentModal,
}) => {
  const [updateCookieConsent] = useMutation(cookieConsentStatisticsUpdateMutation ?? DEFAULT_UPDATE_COOKIE_CONSENT_STATISTICS_MUTATION);
  const handleError = useCallback(
    (error: Error) => {
      if (!shouldSendStatistics()) return;
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        updateCookieConsent({
          onError: () => undefined,
          variables: { key: CookieStatistics.Error },
        });
      }
    },
    [updateCookieConsent],
  );
  if (!process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY) return null;

  return (
    <ConsentManagerBuilder
      mapCustomPreferences={handleMapCustomPreferences}
      onError={handleError}
      writeKey={process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY}>
      {({ saveConsent, destinations, preferences, setPreferences }) => {
        if (onAnalyticsReady && Object.keys(preferences).length > 0) {
          onAnalyticsReady(preferences);
        }

        return (
          <SegmentConsentManager
            saveConsent={saveConsent}
            destinations={destinations}
            preferences={preferences}
            setPreferences={setPreferences}
            termsAndConditions={termsAndConditions}
            imprintAndPrivacy={imprintAndPrivacy}
            cookieConsentStatisticsUpdateMutation={cookieConsentStatisticsUpdateMutation}
            highlightColor={highlightColor}
            hideConsentModal={hideConsentModal}
          />
        );
      }}
    </ConsentManagerBuilder>
  );
};
